import { Flex } from '@chakra-ui/react';
import { WindowManagerContextProvider } from '@components/cockpit/WindowManagerContext';
import MouseFocus from '@components/common/MouseFocus';
import InactivityModal from '@components/InactivityModal';
import ChangeShift from '@components/login/ChangeShift';
import JoystickControl from '@components/video/cameraControl/JoystickControl';
import { useAppSelector } from '@hooks/redux.hooks';
import { useAuth } from '@hooks/useAuth';
import useCheatCode from '@hooks/useCheatCode';
import { selectActiveUser, selectIsChangingShift } from '@redux/authent/authent.selectors';
import {
  selectJoystickControlId,
  selectSelectedDrone,
  selectSelectedNotificationId,
} from '@redux/global/global.selector';
import { skipToken } from '@reduxjs/toolkit/query';
import { useInitModelMutation } from '@services/c2/c2.api';
import { useGetSituationDiffQuery } from '@services/c2/sse.api';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router';

import { CockpitPanelIntlKeysEnum } from './cockpit.types';
import { CockpitUiStateContext } from './CockpitContexts';
import { PANEL_ROUTES } from './CockpitRoutes';
import CockpitTitle from './CockpitTitle';
import Platforms from './panels/platform/Platforms';
import CockpitSidebar from './sidebar/CockpitSidebar';

interface PlatformsPanelContextType {
  isPlatformsPanelOpen: boolean;
  setIsPlatformsPanelOpen: (open: boolean) => void;
}

const PlatformsPanelContext = createContext<PlatformsPanelContextType | undefined>(undefined);

export const usePlatformsPanelContext = (): PlatformsPanelContextType => {
  const context = useContext(PlatformsPanelContext);
  if (!context) {
    throw new Error('usePlatformsPanelContext must be used within a PlatformsPanelContext.Provider');
  }
  return context;
};

export default function Cockpit() {
  const { token } = useAuth();
  const [initModel] = useInitModelMutation();
  const { panel } = useParams();
  const navigate = useNavigate();
  const joystickControlId = useAppSelector(selectJoystickControlId);
  const selectedNotification = useAppSelector(selectSelectedNotificationId);
  const selectedDrone = useAppSelector(selectSelectedDrone);
  const isChangingShift = useAppSelector(selectIsChangingShift);
  const [isSidebarMaximized, setIsSidebarMaximized] = useState(false);

  const { activeSite, activeGroup } = useAppSelector(selectActiveUser);
  const inactivityLimit = activeGroup?.inactivityTime ?? null;
  const openedPanel = useMemo(
    () =>
      (Object.entries(PANEL_ROUTES).find((entry) => entry[1] === panel)?.[0] as CockpitPanelIntlKeysEnum | undefined) ??
      null,
    [panel],
  );

  const [isPlatformsPanelOpen, setIsPlatformsPanelOpen] = useState<boolean>(false);
  const platformContextValue = useMemo(
    () => ({ isPlatformsPanelOpen, setIsPlatformsPanelOpen }),
    [isPlatformsPanelOpen],
  );

  useCheatCode('init SSE model', ['i', 'i', 'i'], () => initModel());

  useGetSituationDiffQuery(
    activeSite && token
      ? {
          site: activeSite.code,
          platforms: activeGroup ? activeGroup.platforms : [],
          token,
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    if (selectedNotification) {
      navigate(PANEL_ROUTES[CockpitPanelIntlKeysEnum.NOTIFICATION]);
    } else if (selectedDrone !== null) {
      navigate(PANEL_ROUTES[CockpitPanelIntlKeysEnum.DRONES_FLEET]);
    }
    //We don't want to trigger this use effect on a navigate change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNotification, selectedDrone]);

  const cockpitUiStateContextValue = useMemo(
    () => ({
      openedPanel,
      isSidebarMaximized,
      setIsSidebarMaximized,
    }),
    [isSidebarMaximized, openedPanel],
  );

  return (
    <WindowManagerContextProvider>
      <PlatformsPanelContext value={platformContextValue}>
        <CockpitUiStateContext value={cockpitUiStateContextValue}>
          <Flex
            height="100%"
            width="100%"
            gap={0}
            backgroundColor="neutral.black"
            border="2px solid"
            borderColor="neutral.black"
          >
            {joystickControlId !== null && <JoystickControl />}
            <MouseFocus />
            <CockpitTitle />
            {inactivityLimit !== null && <InactivityModal inactivityLimit={inactivityLimit} />}
            {isChangingShift && <ChangeShift />}
            {isPlatformsPanelOpen && <Platforms />}
            <CockpitSidebar />
            <Flex position="relative" flex={1} minWidth={0} gap={0.25} wrap="nowrap">
              <Outlet />
            </Flex>
          </Flex>
        </CockpitUiStateContext>
      </PlatformsPanelContext>
    </WindowManagerContextProvider>
  );
}
